import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { amiGet, Card, Kpi, Loading, NotYet, StatusRow, num, pct, usd, isoFmt, errMsg } from "@/lib/ami";

function SystemStatus({ s }) {
  return (
    <Card title="Systemstatus" testid="ami-system-status">
      <StatusRow label="OKX Demo" ok={s.okx_demo.connected} value={s.okx_demo.connected ? s.okx_demo.name : "nicht verbunden"} testid="ami-status-demo" />
      <StatusRow label="OKX Live" value={s.okx_live.connected ? `${s.okx_live.name} · nur lesend` : "nicht verbunden"} warn={s.okx_live.connected} />
      <StatusRow label="Umgebung" ok value="Research (keine Orders)" />
      <StatusRow label="Datenfeed" ok={s.data_feed.ok} warn={!s.data_feed.ok} testid="ami-status-feed"
        value={s.data_feed.last_error || (s.data_feed.latency_ms ? `ok · ${s.data_feed.latency_ms} ms` : "wartet")} />
      <StatusRow label="KI-Modell" value={s.model.status} />
      <StatusRow label="Risk Engine" value={s.risk_engine.status} />
      <StatusRow label="Bot" value={`${s.bot.status} · Live ${s.bot.live_trading}`} />
      <StatusRow label="Historien-Sync" ok={s.sync.history_jobs_active > 0}
        value={s.sync.history_jobs_active ? `${s.sync.history_jobs_active} Job(s) aktiv` : s.sync.last_job ? `zuletzt ${isoFmt(s.sync.last_job.finished_at || s.sync.last_job.created_at)}` : "noch kein Import"} />
      {s.warnings.length > 0 && (
        <div className="mt-4 space-y-1" data-testid="ami-warnings">
          {s.warnings.map((w, i) => <div key={i} className="text-xs text-amber-300/90">{isoFmt(w.at)} · {w.msg}</div>)}
        </div>
      )}
    </Card>
  );
}

function Capital({ a }) {
  if (!a) return <Loading />;
  if (!a.connected)
    return <Card title="Kapital (Demo)" testid="ami-capital"><NotYet text="Keine aktive OKX-Demo-Verbindung." /><Link to="/app/ami/verbindungen" className="mt-3 inline-block text-sm text-primary-foreground underline underline-offset-4" data-testid="ami-connect-link">Verbindung anlegen</Link></Card>;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ami-capital">
      <Kpi label="Gesamtkapital (Demo)" value={usd(a.total_eq_usd)} testid="ami-kpi-equity" />
      <Kpi label="Gebundene Margin" value={usd(a.imr_usd)} />
      <Kpi label="Unrealisierte P&L" value={usd(a.upl_usd)} tone={a.upl_usd > 0 ? "pos" : a.upl_usd < 0 ? "neg" : undefined} />
      <Kpi label="Offene Positionen" value={a.positions.length} hint={`${a.open_orders.length} offene Orders`} />
    </div>
  );
}

function Positions({ a }) {
  if (!a?.connected) return null;
  return (
    <Card title="Aktive Positionen (Demo)" testid="ami-positions">
      {a.positions.length === 0 ? <NotYet text="Keine offenen Positionen." /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm font-mono">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground font-sans"><th className="py-2">Instrument</th><th>Richtung</th><th>Größe</th><th>Entry</th><th>Hebel</th><th>Margin</th><th>uP&L</th><th>Margin-Rendite</th></tr></thead>
          <tbody>{a.positions.map((p, i) => (
            <tr key={i} className="border-t border-border/60"><td className="py-2">{p.inst}</td><td>{p.side}</td><td>{num(p.size, 4)}</td><td>{num(p.entry)}</td><td>{num(p.lever, 0)}x</td><td>{usd(p.margin)}</td><td>{usd(p.upl)}</td><td>{pct(p.upl_ratio_pct)}</td></tr>
          ))}</tbody>
        </table></div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">SL/TP, Risiko und KI-Entscheidung je Position folgen mit der Risk Engine (Phase 4).</p>
    </Card>
  );
}

function MarketMini({ m }) {
  if (!m) return <Loading />;
  return (
    <Card title="BTC-Markt" testid="ami-market-mini" actions={<Link to="/app/ami/live" className="text-xs text-muted-foreground hover:text-foreground">Details</Link>}>
      <div className="font-mono text-3xl" data-testid="ami-btc-price">{num(m.last, 1)} $</div>
      <div className={`font-mono text-sm mt-1 ${m.change24h_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct(m.change24h_pct)} · 24 h</div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div><div className="text-xs text-muted-foreground">Realisierte Vol. 24 h (p.a.)</div><div className="font-mono">{pct(m.volatility?.realized_24h_ann_pct).replace("+", "")}</div></div>
        <div><div className="text-xs text-muted-foreground">Orderbuch-Imbalance ±1 %</div><div className="font-mono">{m.orderbook ? num(m.orderbook.imbalance * 100, 1) + " %" : "–"}</div></div>
        <div><div className="text-xs text-muted-foreground">Funding</div><div className="font-mono">{m.funding ? num(m.funding.rate * 100, 4) + " %" : "–"}</div></div>
        <div><div className="text-xs text-muted-foreground">Open Interest</div><div className="font-mono">{m.open_interest ? num(m.open_interest.btc, 0) + " BTC" : "–"}</div></div>
      </div>
      <div className="mt-4 space-y-2">
        <NotYet text="Marktregime, Marktstruktur, Liquiditätszonen, Squeeze: Phase 2" />
        <NotYet text="Prognosen und Modellstatus: siehe KI-Prognosen" testid="ami-no-model" />
      </div>
    </Card>
  );
}

export default function AmiOverview() {
  const st = useQuery({ queryKey: ["ami-status"], queryFn: () => amiGet("/status"), refetchInterval: 15000 });
  const acc = useQuery({ queryKey: ["ami-account", "demo"], queryFn: () => amiGet("/account", { env: "demo" }), refetchInterval: 30000 });
  const mk = useQuery({ queryKey: ["ami-market", "BTC-USDT-SWAP"], queryFn: () => amiGet("/market", { inst: "BTC-USDT-SWAP" }), refetchInterval: 10000 });
  if (st.isError) return <NotYet text={errMsg(st.error)} />;
  return (
    <div className="space-y-6" data-testid="ami-overview">
      {acc.isError ? <NotYet text={`Konto: ${errMsg(acc.error)}`} testid="ami-account-error" /> : <Capital a={acc.data} />}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">{st.data ? <SystemStatus s={st.data} /> : <Loading />}</div>
        <div className="lg:col-span-5">{mk.isError ? <NotYet text={errMsg(mk.error)} /> : <MarketMini m={mk.data} />}</div>
      </div>
      <Positions a={acc.data} />
    </div>
  );
}
